import { useEffect, useCallback } from 'react';
import * as boardsStore from '../../extensions/bundled/task-manager/boardsStore';
import { closeTaskManagerBoardTab } from '../../store/tabsStore';
import { subscribeBoardChanges } from '../../extensions/bundled/task-manager/boardsUiStore';
import { reconcileBoards } from '../../extensions/bundled/task-manager/boardReconcile';
import { rebaseDirtyBoards } from '../../extensions/bundled/task-manager/boardDiskRebase';
import useBoardHistory from './useBoardHistory';
import { noteBoardEdit } from '../../extensions/bundled/task-manager/boardChangeLog';

/** Saving, reconciling with disk / the sync engine, applying remote board records, undo and updateBoard. */
export function useBoardDiskSync({
  boardsVersion,
  pendingBoardsBumpRef,
  boardId,
  setBoard,
  setBoardLoading,
  selectedTaskId,
  setSelectedTaskId,
  deleteGroupTarget,
  boardModal,
  deleteBoardTarget,
  readOnlyRef,
  setActiveDragId,
  scheduleDebounce,
  loadedBoardIdRef,
  boardIdRef,
  dragActiveRef,
  pullPendingRef,
  reconcileGenRef,
  pendingBoardChangesRef,
  applyBoardRecordsRef,
  reconcileFromDiskRef,
  boardCacheRef,
  dirtyRef,
  dirtyEntityIdsRef,
  markDirtyEntity,
  recomputeSaveState,
  flushSave,
  boardRef,
  undoStackRef,
  redoStackRef,
  syncHistoryFlags,
  resetHistory,
  loadBoards,
}) {
  useEffect(() => {
    // Leaving a board (switch/close) must not drop an edit still sitting in
    // the debounce window; boardRef still points at the outgoing board here.
    flushSave();
    if (!boardId) {
      // Boards-home grid is showing. Keep the last board in memory (and its
      // mounted-but-hidden kanban) so returning to it — or opening another
      // board — reconciles in place instead of remounting dnd-kit, which
      // visibly repositions the cards. loadedBoardIdRef is still cleared so the
      // next open re-runs the (cached, in-place) load + background reconcile.
      loadedBoardIdRef.current = null;
      return;
    }
    if (loadedBoardIdRef.current === boardId) return;
    loadedBoardIdRef.current = boardId;

    const cached = boardCacheRef.current.get(boardId);
    if (cached) {
      // Cache is authoritative for local state: show it instantly (no spinner,
      // cards keyed by id reconcile in place) and do NOT re-read the disk, so a
      // stale disk copy can never overwrite newer local edits. Cross-device
      // changes drop the cache entry (boardsVersion effect) and fall through to
      // the disk read below on the next open.
      boardRef.current = cached;
      setBoard(cached);
      setBoardLoading(false);
      return;
    }

    setBoardLoading(true);
    boardsStore.readBoard(boardId)
      .then((loaded) => {
        if (loadedBoardIdRef.current !== boardId) return; // superseded by a later tab switch
        boardRef.current = loaded || null;
        if (loaded) boardCacheRef.current.set(boardId, loaded);
        setBoard(loaded || null);
      })
      .catch(() => {
        if (loadedBoardIdRef.current !== boardId) return;
        boardRef.current = null;
        setBoard(null);
      })
      .finally(() => {
        if (loadedBoardIdRef.current !== boardId) return;
        setBoardLoading(false);
      });
  }, [boardId, flushSave, boardCacheRef, boardRef, loadedBoardIdRef, setBoard, setBoardLoading]);

  // Transient per-board UI state must not bleed across boards. Without this,
  // TaskBoardTab now staying mounted across tab switches would, on return to a
  // board, re-show a task-detail modal that was open when you left it.
  useEffect(() => {
    setSelectedTaskId(null);
    setActiveDragId(null);
    // A drag cannot survive a board switch; if one was somehow mid-flight,
    // clear the deferral flag so sync re-reads aren't blocked until the next
    // drag, and replay a re-read it deferred: until that reconcile runs,
    // pullPendingRef holds every persist(), so a dropped replay = unsaved edits.
    dragActiveRef.current = false;
    if (pendingBoardsBumpRef.current) { pendingBoardsBumpRef.current = false; reconcileFromDiskRef.current?.(); }
    // History belongs to the board that produced it; a snapshot from board A must
    // never be restorable onto board B.
    resetHistory();
    // The "Saved"/"Saving…" pill is per-board (dirtyRef is keyed by board id);
    // re-derive it for whichever board is now open (or cleared, for the grid).
    recomputeSaveState();
  }, [boardId, resetHistory, recomputeSaveState, dragActiveRef, pendingBoardsBumpRef, reconcileFromDiskRef, setActiveDragId, setSelectedTaskId]);

  // A personal-sync change was pulled from another device. The engine's LWW
  // guard only protects rows already written to SQLite/outbox — NOT edits still
  // inside the 400ms debounce window — so flush those first, then re-read.
  // While a board is open, skip the boards-grid reload entirely: the
  // tab-activation effect above refreshes the grid on the next visit.
  // Extracted from the boardsVersion effect so a re-read deferred during a drag
  // (see dragActiveRef) can be replayed from handleDragEnd/handleDragCancel.
  // Reads all mutable state through refs, so the ref'd copy is never stale.
  const reconcileFromDisk = useCallback(async () => {
    const gen = ++reconcileGenRef.current;
    pullPendingRef.current = true;
    // Rebase dirty boards onto disk BEFORE the flush, or the flush tombstones
    // records the pull just wrote (see boardDiskRebase.js).
    await rebaseDirtyBoards({
      dirty: dirtyRef.current,
      skipIdsFor: (id) => dirtyEntityIdsRef.current.get(id),
      readBoard: boardsStore.readBoard,
      scopeKeyOf: boardsStore.boardScopeKey,
      apply: (id, next) => {
        if (dragActiveRef.current) { pendingBoardsBumpRef.current = true; return; } // redone at the drag-end replay
        dirtyRef.current.set(id, next);
        boardCacheRef.current.set(id, next);
        if (boardRef.current?.id === id) { boardRef.current = next; setBoard(next); }
      },
    }).catch((err) => console.warn('[task-manager] disk rebase before flush failed:', err));
    if (gen !== reconcileGenRef.current) return; // a newer pull's reconcile flushes
    if (dragActiveRef.current) { pendingBoardsBumpRef.current = true; return; }
    pullPendingRef.current = false;
    // Flush local edits first so our own writes are never lost to the re-read.
    flushSave().finally(() => {
      // Read the live boardId (not the callback closure, which is stale if the
      // user switched boards during the async flush) so we never evict or
      // re-read the wrong board.
      const currentBoardId = boardIdRef.current;
      // Any board we hold NO unsaved local edits for may now be stale on disk;
      // drop it so it's re-read fresh when next opened. Dirty boards keep their
      // authoritative in-memory version (local edit wins = LWW).
      for (const id of [...boardCacheRef.current.keys()]) {
        if (id !== currentBoardId && !dirtyRef.current.has(id)) boardCacheRef.current.delete(id);
      }
      if (!currentBoardId) {
        loadBoards();
        return;
      }
      const id = currentBoardId;
      if (dirtyRef.current.has(id)) return; // unsaved local edits win; don't clobber
      boardsStore.readBoard(id)
        .then((loaded) => {
          if (dragActiveRef.current) {
            // A drag started while flushSave/readBoard were in flight: applying
            // now would yank cards mid-gesture. Defer to the drag-end replay,
            // exactly like the boardsVersion effect does.
            pendingBoardsBumpRef.current = true;
            return;
          }
          if (loadedBoardIdRef.current !== id) return; // superseded by tab switch
          if (dirtyRef.current.has(id)) return; // user edited during the read
          if (!loaded) {
            // Deleted on another device: close the tab instead of leaving a
            // perpetual spinner. Not dirty (guarded above), so nothing to lose.
            boardCacheRef.current.delete(id);
            boardRef.current = null;
            closeTaskManagerBoardTab(id);
            return;
          }
          // This effect mostly fires for our OWN sync echoes (the engine's
          // push-ack path bumps boardsVersion after every push), so don't
          // repaint blindly: reconcileBoards returns the CURRENT board object
          // when the disk copy is indistinguishable from what's rendered (skip
          // entirely — no setBoard, no dnd-kit churn), and otherwise preserves
          // object identity for every unchanged task/group so React only
          // touches the cards a remote change actually moved or edited.
          const merged = reconcileBoards(boardRef.current, loaded);
          if (merged === boardRef.current) return; // own echo — nothing changed
          boardRef.current = merged;
          boardCacheRef.current.set(id, merged);
          setBoard(merged);
          // NOTE: deliberately do NOT reset undo history here. This re-read
          // also fires for own-echo reconciles that DO carry a difference (the
          // server answered a conflicted push with merged_fields). Wiping
          // history on that echo made Undo grey out moments after an edit.
          // Keeping history across a re-read is safe: undo is a deliberate user
          // action and sync is field-level LWW. History is still reset on an
          // actual board switch (boardId change).
        })
        .catch(() => {});
    });
  }, [flushSave, loadBoards, boardCacheRef, boardIdRef, boardRef, dirtyEntityIdsRef, dirtyRef, dragActiveRef, loadedBoardIdRef, pendingBoardsBumpRef, pullPendingRef, reconcileGenRef, setBoard]);
  reconcileFromDiskRef.current = reconcileFromDisk;

  useEffect(() => {
    if (boardsVersion === 0) return;
    pullPendingRef.current = true;
    if (dragActiveRef.current) {
      // Mid-drag: applying a re-read now would yank cards out from under the
      // pointer. Defer; handleDragEnd/handleDragCancel replays it — including
      // the board-deleted-remotely path, so that can't be starved.
      pendingBoardsBumpRef.current = true;
      return;
    }
    reconcileFromDisk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardsVersion]);

  // WS-first fast path: paint live team-board records straight into the open
  // board's in-memory state, without waiting for the engine write + disk
  // re-read that drives the boardsVersion effect above (see
  // useTeamBoardsRealtime — that path still runs in parallel and stays
  // authoritative for board-level edits/deletes and any correction needed).
  // Entity-granular: only the specific task/group ids this board still has an
  // unsaved local edit for (dirtyEntityIdsRef) are excluded — everything else
  // (every field on every OTHER card, plus untouched fields read straight off
  // the task prop by an open TaskDetailModal) lands instantly. Full skip is
  // kept only while a drag is in flight (mid-gesture repaint would yank cards
  // out from under the pointer) — and mid-drag publishes are BUFFERED rather
  // than dropped, because dropping them loses data: the record never enters
  // this tab's snapshot, and the drag-end replay flushes that snapshot through
  // the whole-scope writer, which tombstones every record absent from it. That
  // has always been true for a teammate's card added during your drag; it is
  // now also true for an MCP write, which is how it was found.
  const applyBoardRecords = useCallback((scopeKey, records) => {
    const current = boardRef.current;
    if (!current) return;
    if (boardsStore.boardScopeKey(current) !== scopeKey) return;
    const skipIds = dirtyEntityIdsRef.current.get(current.id);
    const patched = boardsStore.patchBoardWithRecords(current, records, { skipIds });
    if (patched === current) return;
    boardRef.current = patched;
    boardCacheRef.current.set(patched.id, patched);
    // If this board has unsaved local edits, the QUEUED SNAPSHOT is what
    // persist() writes — and persist() writes the whole scope, tombstoning
    // every record absent from it. Patching only boardRef would leave the
    // incoming record out of that snapshot and the next flush would delete it.
    // Only refresh an entry that is already dirty: a clean board must not be
    // made dirty by someone else's change.
    if (dirtyRef.current.has(patched.id)) dirtyRef.current.set(patched.id, patched);
    setBoard(patched);
  }, [boardCacheRef, boardRef, dirtyEntityIdsRef, dirtyRef, setBoard]);
  applyBoardRecordsRef.current = applyBoardRecords;

  useEffect(() => {
    return subscribeBoardChanges((scopeKey, records) => {
      if (dragActiveRef.current) {
        pendingBoardChangesRef.current.push([scopeKey, records]);
        return;
      }
      applyBoardRecordsRef.current?.(scopeKey, records);
    });
  }, [applyBoardRecordsRef, dragActiveRef, pendingBoardChangesRef]);

  const {
    pushUndo,
    undo,
    redo,
  } = useBoardHistory({ boardCacheRef, boardId, boardModal, boardRef, deleteBoardTarget, deleteGroupTarget, dirtyRef, markDirtyEntity, recomputeSaveState, redoStackRef, scheduleDebounce, selectedTaskId, setBoard, syncHistoryFlags, undoStackRef });
  const updateBoard = useCallback((updater, { history = true, touch = true } = {}) => {
    // Compute the next board eagerly from boardRef (not via a functional
    // setBoard updater) so the ref, cache, and dirty entry all hold the new
    // board in the same synchronous instant — a flush firing before React's
    // commit then persists the up-to-date board, not a stale one. Eager compute
    // also runs the updater exactly once (StrictMode double-invokes functional
    // updaters, and callers create ids/timestamps inside them).
    const prev = boardRef.current;
    if (!prev) return;
    // Read-only team board (viewer role): swallow all mutations.
    if (readOnlyRef.current) return;
    // touch:false — a change the user did not make (the createdBy fill-in).
    const next = { ...updater(prev), updatedAt: touch ? new Date().toISOString() : prev.updatedAt };
    // What this person changed, field by field: the save writes exactly that.
    noteBoardEdit(prev, next);
    // Record the pre-edit board so this change can be undone. Drag moves pass
    // history:false (they fire per frame) and commit one entry at drag end.
    if (history) {
      pushUndo({ before: prev, after: next });
      syncHistoryFlags();
    }
    boardRef.current = next;
    boardCacheRef.current.set(next.id, next);
    dirtyRef.current.set(next.id, next);
    setBoard(next);
    scheduleDebounce();
    recomputeSaveState();
  }, [scheduleDebounce, pushUndo, syncHistoryFlags, recomputeSaveState, boardCacheRef, boardRef, dirtyRef, readOnlyRef, setBoard]);

  return { pushUndo, undo, redo, updateBoard };
}
